/**
 * Tooltip Positioning Module
 * Implements viewport-aware tooltip positioning with dynamic arrow alignment.
 */

/** Minimum distance from viewport edges */
const VIEWPORT_PADDING = 8;

/** Minimum distance of arrow tip from tooltip edge */
const MIN_ARROW_INSET = 20;

/** X position of arrow tip in the SVG (original orientation, pointing down) */
const SVG_TIP_X = 35;

/** Total width of the arrow SVG element */
const SVG_WIDTH = 57;

/**
* Compute the visible portion of an element by clipping its bounding rect
 * against every ancestor with overflow clipping and the viewport.
 * Returns the intersection rect, or the full rect as fallback if fully clipped.
 */
function getVisibleRect(element: HTMLElement): DOMRect {
    const fullRect = element.getBoundingClientRect();

    let visLeft = fullRect.left;
    let visTop = fullRect.top;
    let visRight = fullRect.right;
    let visBottom = fullRect.bottom;

    let ancestor = element.parentElement;
    while (ancestor) {
        const style = getComputedStyle(ancestor);
        const ox = style.overflowX;
        const oy = style.overflowY;

        if (
            ox === 'hidden' || ox === 'scroll' || ox === 'auto' || ox === 'clip' ||
            oy === 'hidden' || oy === 'scroll' || oy === 'auto' || oy === 'clip'
        ) {
            const aRect = ancestor.getBoundingClientRect();
            if (ox === 'hidden' || ox === 'scroll' || ox === 'auto' || ox === 'clip') {
                visLeft = Math.max(visLeft, aRect.left);
                visRight = Math.min(visRight, aRect.right);
            }
            if (oy === 'hidden' || oy === 'scroll' || oy === 'auto' || oy === 'clip') {
                visTop = Math.max(visTop, aRect.top);
                visBottom = Math.min(visBottom, aRect.bottom);
            }
        }

        ancestor = ancestor.parentElement;
    }

    // Clip against viewport
    visLeft = Math.max(visLeft, 0);
    visTop = Math.max(visTop, 0);
    visRight = Math.min(visRight, window.innerWidth);
    visBottom = Math.min(visBottom, window.innerHeight);

    // If fully clipped, fall back to the original rect
    if (visRight <= visLeft || visBottom <= visTop) {
        return fullRect;
    }

    return new DOMRect(visLeft, visTop, visRight - visLeft, visBottom - visTop);
}

/**
 * Calculate optimal tooltip position relative to a target element.
 * Determines above/below placement based on available viewport space,
 * centers horizontally on the *visible* portion of the target (clamped to
 * viewport), and calculates arrow offset so the pointer aims at the
 * visible center — important for partially-scrolled table column headers.
 */
function positionTooltip(
    targetEl: HTMLElement,
    tooltipContainer: HTMLElement
): { top: number; left: number; arrowSvgLeft: number; placement: 'above' | 'below' } | null {
    if (!targetEl || !tooltipContainer) return null;

    const fullRect = targetEl.getBoundingClientRect();
    const visibleRect = getVisibleRect(targetEl);
    const tooltipRect = tooltipContainer.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    // Measure the arrow element so we can let it overlap into the <th>
    const arrowEl = tooltipContainer.querySelector('.tooltip-arrow') as HTMLElement | null;
    const arrowHeight = arrowEl ? arrowEl.getBoundingClientRect().height : 0;
    // The body height is the tooltip container minus the arrow
    const bodyHeight = tooltipHeight - arrowHeight;

    // --- Horizontal: align left with visible target edge, clamp to viewport ---
    const visibleCenterX = visibleRect.left + visibleRect.width / 2;
    let left = visibleRect.left;
    left = Math.max(
        VIEWPORT_PADDING,
        Math.min(left, viewportWidth - tooltipWidth - VIEWPORT_PADDING)
    );

    // --- Vertical: prefer above, flip to below if necessary ---
    // Position so the body bottom sits at the top of the <th> (above) or
    // body top at the bottom of the <th> (below). The arrow overlaps into
    // the header area.
    const spaceAbove = fullRect.top;
    const spaceBelow = viewportHeight - fullRect.bottom;

    let placement: 'above' | 'below';
    let top: number;

    if (spaceAbove >= bodyHeight) {
        placement = 'above';
        // body bottom at fullRect.top → arrow extends into <th>
        top = fullRect.top - bodyHeight;
    } else if (spaceBelow >= bodyHeight) {
        placement = 'below';
        // arrow overlaps into <th>, body top at fullRect.bottom
        top = fullRect.bottom - arrowHeight;
    } else if (spaceAbove >= spaceBelow) {
        placement = 'above';
        top = Math.max(VIEWPORT_PADDING, fullRect.top - bodyHeight);
    } else {
        placement = 'below';
        top = fullRect.bottom - arrowHeight;
    }

    // --- Arrow offset: visible center relative to tooltip left edge ---
    let arrowOffsetPx = visibleCenterX - left;
    arrowOffsetPx = Math.max(
        MIN_ARROW_INSET,
        Math.min(arrowOffsetPx, tooltipWidth - MIN_ARROW_INSET)
    );

    // SVG left position accounting for tip location within the SVG
    // For 'above': tip is at x=SVG_TIP_X in the original SVG
    // For 'below': SVG is rotated 180°, so tip is at x=(SVG_WIDTH - SVG_TIP_X)
    const svgTipX = placement === 'above' ? SVG_TIP_X : (SVG_WIDTH - SVG_TIP_X);
    const arrowSvgLeft = arrowOffsetPx - svgTipX;

    return { top, left, arrowSvgLeft, placement };
}

/**
 * Tooltip Module for intelligent viewport-aware positioning.
 * Exported as part of window.Etask.Ui.TooltipModule
 */
const TooltipModule = {
    /**
     * Position a tooltip container relative to a target element.
     * Sets fixed-position coordinates, arrow offset via CSS custom property,
     * and placement/positioned data attributes for CSS styling.
     *
     * @param targetEl - The element the tooltip points to
     * @param tooltipContainer - The tooltip wrapper element (position: fixed)
     */
    applyPosition(targetEl: HTMLElement, tooltipContainer: HTMLElement): void {
        if (!targetEl || !tooltipContainer) return;

        const pos = positionTooltip(targetEl, tooltipContainer);
        if (!pos) return;

        tooltipContainer.style.top = `${pos.top}px`;
        tooltipContainer.style.left = `${pos.left}px`;
        tooltipContainer.style.setProperty('--tooltip-arrow-svg-left', `${pos.arrowSvgLeft}px`);
        tooltipContainer.setAttribute('data-tooltip-placement', pos.placement);
        tooltipContainer.setAttribute('data-tooltip-positioned', '');
    }
};

export default TooltipModule;
